import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

interface UIState {
  // Theme
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  setDarkMode: (isDark: boolean) => void;

  // Sidebar
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (isOpen: boolean) => void;

  // Mobile
  isMobileSidebarOpen: boolean;
  toggleMobileSidebar: () => void;
  setMobileSidebarOpen: (isOpen: boolean) => void;

  // Notifications
  notificationCount: number;
  setNotificationCount: (count: number) => void;
  incrementNotificationCount: () => void;
  clearNotifications: () => void;

  // Loading states
  globalLoading: boolean;
  setGlobalLoading: (isLoading: boolean) => void;

  // Business-line selection (inventory layer, v1.4 FR-37/RR-6).
  // Persisted as {userId, lineId}; consumers discard it when the stored
  // userId differs from the logged-in user (shared-machine mitigation).
  businessLineSelection: { userId: string; lineId: string } | null;
  setBusinessLineSelection: (selection: { userId: string; lineId: string } | null) => void;
}

export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set) => ({
        // Theme
        isDarkMode: false,
        toggleDarkMode: () => set((state) => ({ isDarkMode: !state.isDarkMode })),
        setDarkMode: (isDark) => set({ isDarkMode: isDark }),

        // Sidebar
        isSidebarOpen: true,
        toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),
        setSidebarOpen: (isOpen) => set({ isSidebarOpen: isOpen }),

        // Mobile
        isMobileSidebarOpen: false,
        toggleMobileSidebar: () =>
          set((state) => ({ isMobileSidebarOpen: !state.isMobileSidebarOpen })),
        setMobileSidebarOpen: (isOpen) => set({ isMobileSidebarOpen: isOpen }),

        // Notifications
        notificationCount: 0,
        setNotificationCount: (count) => set({ notificationCount: count }),
        incrementNotificationCount: () =>
          set((state) => ({ notificationCount: state.notificationCount + 1 })),
        clearNotifications: () => set({ notificationCount: 0 }),

        // Loading states
        globalLoading: false,
        setGlobalLoading: (isLoading) => set({ globalLoading: isLoading }),

        // Business-line selection (inventory layer)
        businessLineSelection: null,
        setBusinessLineSelection: (selection) => set({ businessLineSelection: selection }),
      }),
      {
        name: 'ui-storage',
        partialize: (state) => ({
          isDarkMode: state.isDarkMode,
          isSidebarOpen: state.isSidebarOpen,
          businessLineSelection: state.businessLineSelection,
        }),
      },
    ),
    {
      name: 'ui-store',
    },
  ),
);
